import { v4 } from "uuid";
import { DesiredStateItem } from "../config";
import logger from "../utils/Logger";
import { TimerManager } from "../utils/timer";
import { recursiveCompare, safeObjCopy } from "../utils/Json";


export enum Result {
    /** Controller confirmed the state change within the `pendingRequestTimeoutMs` */
    Confirmed = "Confirmed",
    /** Controller did not confirm the state change within the `pendingRequestTimeoutMs` */
    Expired = "Expired",
    /** Sending state state is not possible. User needs to confirm. */
    Restricted = "Restricted",
    /** Control Change request has been replaced by a subsequent one. */
    Replaced = "Replaced",
    /** Could not send it to the cloud */
    FailedToSend = "FailedToSend",
}

type PendingRequest = {
    /** Timestamp the request has sent */
    time: Date,
    desiredState: DesiredStateItem,
    timer: ReturnType<typeof setTimeout>,
    BatchUUID: string,
    resolve: (disposition: Result) => void,
    reject: (reason?: any) => void,
}

export type PendingRequestView = {
    /** Timestamp the request has sent */
    time: Date,
    desiredState: DesiredStateItem,
    BatchUUID: string,
}

/**
 * Handles sending desired state changes.
 * Usage:
 * ```
 * const manager = new DesiredStateManager(...);
 * // Add items to the queue
 * manager.addPendingRequest();
 * // Start the queue
 * mnager.startSendingPendingRequests();
 * // In case desired states has changed, call checkPendingRequests();
 * // It detects which pending reuest items has been confirmed by the remote side
 * // and resolves them, then triggers onUpdate();
 * manager.checkPendingReqeusts();
 * // Call destroy to clean up timers.
 * manager.destroy();
 * ```
 */
export default class DesiredStateManager {
    /** Time window while a pending user request overrides desired states received from the controller */
    private pendingRequestTimeoutMs = 1000 * 60 * 2;
    /** Maximum delay of sending a desired state change queue */
    private queueDelayMs = 1000;

    /** Desired state changes queue */
    private pendingRequests: PendingRequest[] = []; 
    private sendPendingRequestsCounter = 0;

    private timerManager = new TimerManager();

    private lastSendStarted = new Date(0);

    constructor(
        private getDesiredStates: () => readonly DesiredStateItem[],
        private getDesiredStatChangesConfirmedByController: () => readonly string[],
        private setDesiredStates: (desiredStates: DesiredStateItem[], BatchUUID: string) => Promise<void>,
        private onUpdate: () => void,
        private onError: (err: Error) => void,
        overrideDefaultPendingRequestTimeoutMs: number,
    ) {
        if (overrideDefaultPendingRequestTimeoutMs) {
            this.pendingRequestTimeoutMs = overrideDefaultPendingRequestTimeoutMs;
        }
    }

    public destroy() {
        this.timerManager.clearAll();
    }

    private getPendingRequestIndex(ControlUUID: string) {
        return this.pendingRequests.findIndex(
            item => item.desiredState.ControlUUID === ControlUUID
        );
    }

    public addPendingRequest(
        desiredState: DesiredStateItem, 
        resolve: ((disposition: Result) => void),
        reject: ((reason?: any) => void),
    ) {
        if (!desiredState) {
            throw new Error("Missing argument: desiredState");
        } else if (!desiredState.ControlUUID) {
            throw new Error("Missing argument: desiredState.ControlUUID");
        }
        // Clone to prevent external mutation of internal state
        desiredState = safeObjCopy(desiredState);
        const index = this.getPendingRequestIndex(desiredState.ControlUUID);
        if (index >= 0) {
            this.removePendingRequest(desiredState.ControlUUID, Result.Replaced);
        }
        this.pendingRequests.push({
            time: new Date(),
            desiredState,
            timer: undefined,
            BatchUUID: undefined,
            resolve,
            reject,
        });
        logger.verbose('[DesiredStateManager] addPendingRequest():', desiredState.ControlUUID.slice(-12));
    }

    public getPendingRequests(): readonly PendingRequestView[] {
        // Return a read-only snapshot of this.pendingRequests
        // without internal control fields (resolve, reject, timer)
        // to preserve encapsulation and prevent external mutation.
        return this.pendingRequests.map(({ time, desiredState, BatchUUID }) => ({
            time,
            desiredState: safeObjCopy(desiredState),
            BatchUUID,
        }));
    }
     
    private removePendingRequest(ControlUUID: string, reason: Result) {
        const index = this.getPendingRequestIndex(ControlUUID);
        if (index >= 0) {
            const pendingRequest = this.pendingRequests[ index ];
            this.timerManager.clear(pendingRequest.timer);
            const currentDesiredState = this.getDesiredStates().find((item) => item.ControlUUID === ControlUUID);
            const diff = recursiveCompare(pendingRequest.desiredState, currentDesiredState);
            logger.verbose('[DesiredStateManager] removePendingRequest() removing:',  ControlUUID.slice(-12), ' at index:', index, "reason:", reason, "diff:", diff);
            this.pendingRequests.splice(index, 1);
            pendingRequest.resolve(reason);
        }
    }

    public startSendingPendingRequests() {
        if (!this.pendingRequests.filter(item => !item.timer)?.length) {
            logger.debug('[DesiredStateManager] startSendingPendingRequests() No unsent pending requests.');
            return;
        }
        const diff = (new Date()).getTime() - this.lastSendStarted.getTime();
        if (diff > this.queueDelayMs) {
            logger.debug('[DesiredStateManager] startSendingPendingRequests() trigger startSendingPendingRequests()');
            this.sendPendingRequests();
        } else {
            logger.debug('[DesiredStateManager] startSendingPendingRequests() schedule startSendingPendingRequests() to run in:', diff / 1000, "s.");
            this.timerManager.add(setTimeout(() => this.sendPendingRequests(), diff));
        }
    }

    private onPendingRequestTimeout(pendingRequest: PendingRequest) {
        logger.verbose('[DesiredStateManager] onPendingRequestTimeout():', pendingRequest.desiredState.ControlUUID.slice(-12));
        this.removePendingRequest(pendingRequest.desiredState.ControlUUID, Result.Expired);
        // Update button state
        this.onUpdate();
    }

    public checkPendingReqeusts() {
        const desiredStateChangesConfirmedByController = this.getDesiredStatChangesConfirmedByController();
        this.pendingRequests
            .filter(item => desiredStateChangesConfirmedByController.includes(item.BatchUUID))
            .forEach(item => {
                logger.debug("[DesiredStateManager] checkPendingReqeusts() confirmed:", item);
                this.removePendingRequest(item.desiredState.ControlUUID, Result.Confirmed);
            })
        ;
    }

    private sendPendingRequests() {
        this.lastSendStarted = new Date();
        const counter = ++this.sendPendingRequestsCounter;
        const traceId = v4();

        const desiredStateChangesToSend = [];
        this.pendingRequests.filter(item => !item.timer)?.forEach(itemPendingRequest => {
            itemPendingRequest.timer = this.timerManager.add(
                setTimeout(this.onPendingRequestTimeout.bind(this, itemPendingRequest), this.pendingRequestTimeoutMs)
            );
            itemPendingRequest.BatchUUID = traceId;
            const targetDesiredState = itemPendingRequest.desiredState;
            desiredStateChangesToSend.push(targetDesiredState);

            const currentDesiredState = this.getDesiredStates().find(item => item.ControlUUID === targetDesiredState.ControlUUID);
            const diff = recursiveCompare(targetDesiredState, currentDesiredState);
            logger.debug('[DesiredStateManager][' + counter +'] sendPendingRequests() ' + targetDesiredState.ControlUUID.slice(-12) + ' C/T diff:',
                diff
                    .map(item => (
                        item.path
                        + (item.diff !== "value" ? " (" + item.diff + ")" : "")
                        + ": " + item.reference + "/" + item.input)
                    )?.join(", ")
            )
            if (!diff?.length) {
                // Even if the desired states to send have no difference compared to the actual Controller state the App is aware of
                // it still needs to be sent because the Controller state could still be differet, it's update has just not arrived yet probably.
            }
        })
        
        if (!desiredStateChangesToSend.length) {
            logger.debug('[DesiredStateManager][' + counter + '] sendPendingRequests() no pending request without timer set.');
            return;
        }

        logger.debug('[DesiredStateManager][' + counter + '] sendPendingRequests() trigger sending changes of controls:', 
            desiredStateChangesToSend.map(item =>
                item.ControlUUID.slice(-12) + ': ' + item.Status + ' ' + item.SetPoint + ' ' + item.PowerLevel
            ).join(", ")
        );
        return this.setDesiredStates(desiredStateChangesToSend, traceId)
            .then(res => {
                logger.debug('[DesiredStateManager][' + counter + '] sendPendingRequests() Result:', res);
            })
            .catch(e => {
                logger.info('[DesiredStateManager][' + counter + '] sendPendingRequests() error:', e, "desiredStateChangesToSend:", desiredStateChangesToSend);

                // Remove pending requests if sending the request failed
                // (And the pending request was not updated since then by a subsequent button press)
                this.pendingRequests
                    .filter(item => item.BatchUUID === traceId)
                    .forEach(item => {
                        this.removePendingRequest(item.desiredState.ControlUUID, Result.FailedToSend);
                    })
                ;
                e.msgForDev = (e.msgForDev ? (e.msgForDev + " ") : "Controls: ")
                    + desiredStateChangesToSend.map(item => item.ControlUUID).join(", ")
                ;
                this.onUpdate();
                this.onError(e);
            })
        ;
    }

}


type ConfirmedBatchUuidItems = {
    added: number,
    items: string[],
};
/**
 * Created for managing the BatchUUDs that the Controller sends to the Cloud-API via the Device.uploadDsiredState, and the Cloud-API publishes as Site.setDesiredState
 * Utility class for managing arrays arrived in batches.
 * Provides method
 * - add batches
 * - get all that was received so far
 * - remove old ones
 */
export class BatchUuidManager {
    /** An array containing the BatchUUIDs used for Site.setDesiredState RPC calls that were eventually acknowledged as processed by the Controller. */
    desiredStateChangesConfirmedByController: ConfirmedBatchUuidItems[] = [];
    /** Max time intervall to hold the itmes in desiredStateChangesConfirmedByController */
    desiredStateChangesConfirmedByControllerExpirationTimeMs = 1000 * 60 * 10;

    getDesiredStatChangesConfirmedByController() {
        const result = [];
        for (const item of this.desiredStateChangesConfirmedByController) {
            result.push(...item.items);
        }
        return result;
    }

    addDesiredStatChangesConfirmedByController(items: string[]) {
        const expirationTime = Date.now() - this.desiredStateChangesConfirmedByControllerExpirationTimeMs;
        this.desiredStateChangesConfirmedByController = this.desiredStateChangesConfirmedByController.filter(
            item => item.added > expirationTime
        );
        this.desiredStateChangesConfirmedByController.push({
            added: Date.now(),
            items
        });
    }
}
